<div class="container min-h-full flex flex-col mx-auto px-4 md:px-6 pt-8 pb-12">
  <!-- Page Header -->
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
    <div>
      <h2 class="text-2xl font-semibold text-gray-900 tracking-tight">
        Supporttoegang
      </h2>
      <p class="text-sm text-gray-600 mt-1 max-w-2xl">
        Geef supportmedewerkers tijdelijk toegang tot jouw omgeving. Bekijk wie er
        momenteel actief is en raadpleeg eerdere sessies.
      </p>
    </div>
    <div class="shrink-0">
      <button
        *appHasPermission="'support_session:create'"
        mat-flat-button
        type="button"
        (click)="openAddForm()"
      >
        <mat-icon>add</mat-icon>
        Nieuwe code
      </button>
    </div>
  </div>

  <!-- Active Support Sessions Section -->
  <div class="mb-10">
    <div class="mb-2">
      <div class="flex items-center gap-3">
        <h3 class="text-lg font-semibold text-gray-900">Actieve support sessies</h3>
        @if (activeSessionsCount() > 0) {
          <span
            class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            {{ activeSessionsCount() }} actief
          </span>
        }
      </div>
      <p class="text-sm text-gray-600 mt-0.5">
        Supportmedewerkers die momenteel toegang hebben tot jouw omgeving.
      </p>
    </div>

    @if (activeSessionsCount() > 0) {
      <div class="grid gap-4 w-full">
        @for (session of activeSessions(); track session.id) {
          <div
            class="bg-white rounded-lg shadow-xs transition-all hover:shadow-sm overflow-hidden"
          >
            <!-- Card content -->
            <div
              class="p-4 flex flex-col xl:flex-row xl:items-start justify-between gap-4"
            >
              <div class="flex items-start gap-3.5">
                <!-- Avatar initials without border -->
                <div
                  class="w-11 h-11 rounded-full bg-slate-100 text-slate-700 font-semibold flex items-center justify-center text-sm shrink-0"
                >
                  {{ getInitials(session.crmDisplayName) }}
                </div>
                <div>
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-semibold text-gray-900 text-base">{{
                      session.crmDisplayName
                    }}</span>
                    <div class="flex items-center gap-1 flex-wrap">
                      @for (roleLabel of session.scope.roleLabels; track roleLabel) {
                        <span
                          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                        >
                          {{ roleLabel }}
                        </span>
                      }
                    </div>
                  </div>
                  <div class="text-sm text-gray-700 mt-1">
                    {{ session.scope.unitName ?? 'Alle units' }}
                  </div>
                  @if (session.reason) {
                    <div
                      class="mt-2 pl-3 border-l-2 border-indigo-400 py-0.5 text-xs text-gray-600 italic"
                    >
                      <span class="font-medium not-italic text-gray-800">Reden:</span>
                      "{{ session.reason }}"
                    </div>
                  }
                  <div
                    class="text-xs text-gray-500 mt-3 flex items-center gap-2 flex-wrap"
                  >
                    <span>Gestart {{ getTimeAgoText(session.createdAt) }}</span>
                    <span>•</span>
                    <span
                      >Verloopt over {{ getRemainingTimeText(session.expiresAt) }}</span
                    >
                  </div>
                </div>
              </div>

              <!-- End session action with standard icon color -->
              <button
                *appHasPermission="'support_session:create'"
                mat-stroked-button
                type="button"
                class="[--mat-sys-primary:var(--color-red-600)] [--mdc-outlined-button-outline-color:var(--color-red-600)] shrink-0 w-full xl:w-auto justify-center"
                (click)="handleEnd(session)"
              >
                <mat-icon>logout</mat-icon>
                Sessie beëindigen
              </button>
            </div>

            <!-- Footer status line -->
            <div
              class="px-4 py-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500"
            >
              <span class="relative flex h-2 w-2 shrink-0">
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"
                ></span>
              </span>
              Support heeft momenteel toegang
            </div>
          </div>
        }
      </div>
    } @else {
      <div
        class="bg-white rounded-lg py-12 px-6 text-center flex flex-col items-center justify-center w-full"
      >
        <div
          class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 mb-3"
        >
          <mat-icon>schedule</mat-icon>
        </div>
        <h4 class="font-semibold text-gray-900 text-base mb-1">
          Geen actieve supportsessies
        </h4>
        <p class="text-sm text-gray-500 max-w-md">
          Support heeft momenteel geen toegang tot jouw omgeving.
        </p>
      </div>
    }
  </div>

  <!-- Session History Section -->
  <div class="mb-10">
    <div class="flex items-center justify-between gap-4 mb-2">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">Sessie historie</h3>
        <p class="text-sm text-gray-600 mt-0.5">
          Eerdere support sessies voor jouw omgeving.
        </p>
      </div>
    </div>

    <app-data-table-container class="rounded-lg overflow-hidden max-h-105">
      <table mat-table [dataSource]="paginatedSessions()">
        <!-- Agent Column -->
        <ng-container matColumnDef="agent" sticky>
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Medewerker
          </th>
          <td mat-cell *matCellDef="let element">
            <div class="py-2.5 flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-semibold flex items-center justify-center text-xs shrink-0"
              >
                {{ getInitials(element.crmDisplayName) }}
              </div>
              <div>
                <div class="font-medium text-gray-900 text-sm">
                  {{ element.crmDisplayName }}
                </div>
                <div class="text-xs text-gray-500">RobotCtrl support</div>
              </div>
            </div>
          </td>
        </ng-container>

        <!-- Reason Column -->
        <ng-container matColumnDef="reason">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Reden van toegang
          </th>
          <td mat-cell *matCellDef="let element">
            <span
              class="text-sm text-gray-700 max-w-xs truncate block"
              [matTooltip]="element.reason"
            >
              {{ element.reason || '—' }}
            </span>
          </td>
        </ng-container>

        <!-- Scope Column -->
        <ng-container matColumnDef="scope">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Omgeving
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm text-gray-800">{{
              element.scope.unitName ?? 'Alle units'
            }}</span>
          </td>
        </ng-container>

        <!-- Role Column -->
        <ng-container matColumnDef="role">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Rol
          </th>
          <td mat-cell *matCellDef="let element">
            <div
              class="flex items-center gap-1.5 flex-nowrap"
              [matTooltip]="element.scope.roleLabels.join(', ')"
            >
              @if (element.scope.roleLabels.length > 2) {
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                >
                  {{ element.scope.roleLabels[0] }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 cursor-help"
                >
                  +{{ element.scope.roleLabels.length - 1 }} meer
                </span>
              } @else {
                @for (roleLabel of element.scope.roleLabels; track roleLabel) {
                  <span
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                  >
                    {{ roleLabel }}
                  </span>
                }
              }
            </div>
          </td>
        </ng-container>

        <!-- Started Column -->
        <ng-container matColumnDef="started">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Gestart op
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm text-gray-700">{{
              element.createdAt | date: 'dd MMM yyyy HH:mm'
            }}</span>
          </td>
        </ng-container>

        <!-- Duration Column -->
        <ng-container matColumnDef="duration">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Sessieduur
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm text-gray-600">{{
              getSessionDurationText(element)
            }}</span>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Status
          </th>
          <td mat-cell *matCellDef="let element">
            @if (isLive(element)) {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Actief
              </span>
            } @else if (element.status === 'ended') {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                Beëindigd
              </span>
            } @else {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Verlopen
              </span>
            }
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          ></th>
          <td mat-cell *matCellDef="let element">
            <div class="flex justify-end">
              @if (isLive(element)) {
                <button
                  *appHasPermission="'support_session:create'"
                  mat-icon-button
                  type="button"
                  matTooltip="Sessie beëindigen"
                  (click)="handleEnd(element)"
                >
                  <mat-icon>logout</mat-icon>
                </button>
              }
            </div>
          </td>
        </ng-container>

        <tr
          mat-header-row
          *matHeaderRowDef="sessionColumns; sticky: true"
          class="whitespace-nowrap"
        ></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: sessionColumns"
          class="whitespace-nowrap"
        ></tr>

        <tr class="mat-mdc-row" *matNoDataRow>
          <td
            class="mat-mdc-cell mdc-data-table__cell !border-none"
            [colSpan]="sessionColumns.length"
          >
            <div class="py-10 px-6 text-center flex flex-col items-center justify-center">
              <div
                class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 mb-2"
              >
                <mat-icon>history</mat-icon>
              </div>
              <div class="font-semibold text-gray-900 text-sm mb-0.5">
                Geen sessie historie
              </div>
              <div class="text-xs text-gray-500">
                Support heeft nog niet ingelogd op jouw omgeving.
              </div>
            </div>
          </td>
        </tr>
      </table>

      @if (!hasNoSessions()) {
        <mat-paginator
          class="border-t border-(--mat-table-row-item-outline-color)"
          [length]="sessions().length"
          [pageIndex]="sessionPage()"
          [pageSize]="sessionPageSize()"
          [pageSizeOptions]="[5, 10, 25]"
          (page)="handleSessionPageChange($event)"
        ></mat-paginator>
      }
    </app-data-table-container>
  </div>

  <!-- Support Access Codes Section -->
  <div class="mb-10">
    <div class="mb-2">
      <h3 class="text-lg font-semibold text-gray-900">Support toegangscodes</h3>
      <p class="text-sm text-gray-600 mt-0.5">
        Codes die je hebt aangemaakt voor support toegang.
      </p>
    </div>

    <app-data-table-container class="rounded-lg overflow-hidden max-h-105">
      <table mat-table [dataSource]="paginatedCodes()">
        <!-- Created Column -->
        <ng-container matColumnDef="created" sticky>
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Aangemaakt op
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm text-gray-700">{{
              element.createdAt | date: 'dd MMM yyyy HH:mm'
            }}</span>
          </td>
        </ng-container>

        <!-- Created By Column -->
        <ng-container matColumnDef="createdBy">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Aangemaakt door
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm font-medium text-gray-900">{{
              element.createdByDisplayName ?? 'Gebruiker'
            }}</span>
          </td>
        </ng-container>

        <!-- Scope Column -->
        <ng-container matColumnDef="scope">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Omgeving
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm text-gray-800">{{
              element.scope.unitName ?? 'Alle units'
            }}</span>
          </td>
        </ng-container>

        <!-- Role Column -->
        <ng-container matColumnDef="role">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Rol
          </th>
          <td mat-cell *matCellDef="let element">
            <div
              class="flex items-center gap-1.5 flex-nowrap"
              [matTooltip]="element.scope.roleLabels.join(', ')"
            >
              @if (element.scope.roleLabels.length > 2) {
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                >
                  {{ element.scope.roleLabels[0] }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 cursor-help"
                >
                  +{{ element.scope.roleLabels.length - 1 }} meer
                </span>
              } @else {
                @for (roleLabel of element.scope.roleLabels; track roleLabel) {
                  <span
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                  >
                    {{ roleLabel }}
                  </span>
                }
              }
            </div>
          </td>
        </ng-container>

        <!-- Duration Column -->
        <ng-container matColumnDef="duration">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Geldigheid
          </th>
          <td mat-cell *matCellDef="let element">
            <span class="text-sm text-gray-600">{{
              getDurationText(element.createdAt, element.expiresAt)
            }}</span>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          >
            Status
          </th>
          <td mat-cell *matCellDef="let element">
            @if (isUsable(element)) {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Actief
              </span>
            } @else if (element.status === 'redeemed') {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Gebruikt
              </span>
            } @else if (element.status === 'revoked') {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-700"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                Ingetrokken
              </span>
            } @else {
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Verlopen
              </span>
            }
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="!text-xs !font-semibold !text-gray-500 !uppercase tracking-wider"
          ></th>
          <td mat-cell *matCellDef="let element">
            <div class="flex justify-end">
              @if (isUsable(element)) {
                <button
                  *appHasPermission="'support_session:create'"
                  mat-icon-button
                  type="button"
                  matTooltip="Code intrekken"
                  (click)="handleRevoke(element)"
                >
                  <mat-icon>block</mat-icon>
                </button>
              }
            </div>
          </td>
        </ng-container>

        <tr
          mat-header-row
          *matHeaderRowDef="displayedColumns; sticky: true"
          class="whitespace-nowrap"
        ></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="whitespace-nowrap"
        ></tr>

        <tr class="mat-mdc-row" *matNoDataRow>
          <td
            class="mat-mdc-cell mdc-data-table__cell !border-none"
            [colSpan]="displayedColumns.length"
          >
            <div class="py-10 px-6 text-center flex flex-col items-center justify-center">
              <div
                class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 mb-2"
              >
                <mat-icon>key</mat-icon>
              </div>
              <div class="font-semibold text-gray-900 text-sm mb-0.5">
                Geen toegangscodes
              </div>
              <div class="text-xs text-gray-500">
                Er zijn nog geen codes aangemaakt voor support toegang.
              </div>
            </div>
          </td>
        </tr>
      </table>

      @if (!hasNoData()) {
        <mat-paginator
          class="border-t border-(--mat-table-row-item-outline-color)"
          [length]="codes().length"
          [pageIndex]="codePage()"
          [pageSize]="codePageSize()"
          [pageSizeOptions]="[5, 10, 25]"
          (page)="handleCodePageChange($event)"
        ></mat-paginator>
      }
    </app-data-table-container>
  </div>

  <!-- Security & Information Card -->
  <div class="bg-white rounded-lg p-6">
    <div class="flex items-center gap-2.5 mb-4">
      <div
        class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"
      >
        <mat-icon class="text-indigo-700">shield</mat-icon>
      </div>
      <div>
        <h4 class="font-semibold text-gray-900 text-base">Over supporttoegang</h4>
        <p class="text-xs text-gray-500">
          Veilig en transparant samenwerken met Robot-Ctrl support.
        </p>
      </div>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-gray-600">
      <div
        class="bg-slate-50 rounded-lg p-4 border border-slate-100 flex flex-col gap-1.5"
      >
        <div class="font-semibold text-gray-900 flex items-center gap-2">
          <mat-icon class="text-indigo-600 text-base">lock</mat-icon>
          Volledige controle
        </div>
        <p class="text-gray-600 leading-relaxed">
          Support kan alleen inloggen met een door jou aangemaakte en actieve
          toegangscode.
        </p>
      </div>

      <div
        class="bg-slate-50 rounded-lg p-4 border border-slate-100 flex flex-col gap-1.5"
      >
        <div class="font-semibold text-gray-900 flex items-center gap-2">
          <mat-icon class="text-indigo-600 text-base">schedule</mat-icon>
          Tijdelijke duur
        </div>
        <p class="text-gray-600 leading-relaxed">
          Elke sessie verloopt automatisch na afloop van de gekozen geldigheidsduur.
        </p>
      </div>

      <div
        class="bg-slate-50 rounded-lg p-4 border border-slate-100 flex flex-col gap-1.5"
      >
        <div class="font-semibold text-gray-900 flex items-center gap-2">
          <mat-icon class="text-indigo-600 text-base">admin_panel_settings</mat-icon>
          Specifieke rechten
        </div>
        <p class="text-gray-600 leading-relaxed">
          Jij bepaalt met welke rollen en voor welke unit de supportmedewerker inlogt.
        </p>
      </div>

      <div
        class="bg-slate-50 rounded-lg p-4 border border-slate-100 flex flex-col gap-1.5"
      >
        <div class="font-semibold text-gray-900 flex items-center gap-2">
          <mat-icon class="text-indigo-600 text-base">history_edu</mat-icon>
          Audit logboek
        </div>
        <p class="text-gray-600 leading-relaxed">
          Alle acties van supportmedewerkers worden transparant geregistreerd in het
          auditlog.
        </p>
      </div>
    </div>
  </div>
</div>
